import { Routes } from '@euler/app/RoutesAdapter';
import { AppStatusBarContextProvider } from '@euler/app/components/AppStatusBar';
import { LaunchScreen } from '@euler/app/components/LaunchScreen';
import { MaintenanceScreen } from '@euler/app/components/MaintenanceScreen';
import {
  PresentationModeProvider,
  usePresentationMode,
} from '@euler/app/components/PresentationModeContext';
import { WiredScreenSaver, appTouch$ } from '@euler/app/components/ScreenSaver';
import { SessionTracker } from '@euler/app/components/SessionTracker';
import { TokenRefreshScheduler } from '@euler/app/components/TokenRefreshScheduler';
import { TrackVersionUpgrade } from '@euler/app/components/TrackVersionUpgrade';
import { Tracking } from '@euler/app/components/Tracking';
import { UpdatesCheck } from '@euler/app/components/UpdatesCheck';
import { AIAssistant } from '@euler/app/components/ai/AIAssistant';
import { AIAssistantProvider } from '@euler/app/components/ai/AIAssistantProvider';
import { ImageViewerContextProvider } from '@euler/app/components/media/ImageViewerContext';
import { AppModalProvider } from '@euler/app/components/modal/AppModal';
import '@euler/app/components/reset';
import { FlushReportSharingLogs } from '@euler/app/components/sharing/FlushReportSharingLogs';
import { VoiceRecordingProvider } from '@euler/app/components/voice/VoiceRecordingProvider';
import {
  AuthContextProvider,
  useCurrentUser,
} from '@euler/app/flows/auth/context';
import { useAuthContextKey } from '@euler/app/flows/auth/hooks';
import { FloatingChatHost } from '@euler/app/flows/chat/lib/floating/FloatingChatHost';
import { FloatingChatProvider } from '@euler/app/flows/chat/lib/floating/provider';
import { ChatSessionProvider } from '@euler/app/flows/chat/lib/session/provider';
import { ChatUserIdentity } from '@euler/app/flows/chat/lib/types';
import { makePlaceholderChatIdentity } from '@euler/app/flows/chat/utils/makePlaceholderChatIdentity';
import { FeedUnreadProvider } from '@euler/app/flows/feed/lib/unread/provider';
import { WeWorkRegistration } from '@euler/app/integrations/WeWorkRegistration';
import { RoutesContainer } from '@euler/app/navigation/RoutesContainer';
import { NotificationHandlers } from '@euler/app/notification/NotificationHandlers';
import { getColorTheme } from '@euler/app/theming';
import { useColorMode } from '@euler/app/theming/color-mode';
import {
  PresentationThemeProvider,
  kDefaultPresentationColorMode,
  usePresentationTheme,
} from '@euler/app/tv/theme';
import { Fonts } from '@euler/components/Fonts';
import { useIcomoon } from '@euler/components/Icomoon';
import { UseHook } from '@euler/components/UseHook';
import { ErrorFallback } from '@euler/components/error';
import { FloatingTextInput } from '@euler/components/input/FloatingTextInput';
import { LoadingProvider } from '@euler/components/loading/Provider';
import { WiredSnackbar } from '@euler/components/snackbar/Snackbar';
import { config } from '@euler/config';
import { ActiveEntityProvider } from '@euler/functions/ActiveEntityContext';
import { TaskStateTrackingProvider } from '@euler/functions/TaskStateTrackingContext';
import { useIsHub } from '@euler/functions/isHub';
import {
  checkSystemStatus,
  maintenanceResolved$,
} from '@euler/functions/maintenanceMode';
import { ResponsiveContextProvider } from '@euler/functions/responsive/ResponsiveContext';
import { useEcho } from '@euler/functions/useEcho';
import { useLockScreenOrientation } from '@euler/functions/useLockScreenOrientation';
import { useTrackingTransparency } from '@euler/functions/useTrackingTransparency';
import {
  TouchEventBoundary as TouchEventBoundarySentryImpl,
  withErrorBoundary as withErrorBoundarySentryImpl,
} from '@euler/lib/integration/sentry';
import { useWeixinIntegration } from '@euler/lib/integration/weixin';
import { AutomaticKeyboardMonitor } from '@euler/lib/keyboard';
import { clearIos18VideoAssetsCache } from '@euler/lib/media/MediaService';
import { ServiceFactoryProvider } from '@euler/services/factory';
import { delayed } from '@euler/utils';
import { ActionSheetProvider } from '@expo/react-native-action-sheet';
import * as SplashScreen from 'expo-splash-screen';
import { StatusBar } from 'expo-status-bar';
import {
  PermissionStatus,
  getTrackingPermissionsAsync,
  requestTrackingPermissionsAsync,
} from 'expo-tracking-transparency';
import React, {
  ComponentType,
  Fragment,
  ReactNode,
  Suspense,
  memo,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { Platform } from 'react-native';
import ReactNativeErrorBoundary, {
  FallbackComponentProps,
} from 'react-native-error-boundary';
import { GestureHandlerRootView } from 'react-native-gesture-handler';
import { RootSiblingParent } from 'react-native-root-siblings';
import { SafeAreaProvider } from 'react-native-safe-area-context';
import { Slot, Provider as SlotFillProvider } from 'react-slot-fill';
import { ThemeProvider } from 'styled-components';
import { HoldItemProvider } from './components/holdmenu/HoldMenu';

// No native splash screen registered for given view controller. Call 'SplashScreen.show'
// for given view controller first, see below links for more information:
// - https://github.com/expo/expo/issues/7689
// - https://github.com/expo/expo/search?q=No+native+splash+screen+registered+for+given+view+controller.+Call+%27SplashScreen.show%27+for+given+view+controller+first.&type=issues
if (!__DEV__) {
  SplashScreen.preventAutoHideAsync()
    .then(res => {
      __DEV__ && console.log('splashscreen autohide prevent result: ', res);
    })
    .catch(err => {
      console.error('error prevent splashscreen from auto hide: ', err);
    });
}

export const App = () => {
  useEffect(() => {
    if (Platform.OS !== 'ios') return;
    void getTrackingPermissionsAsync().then(res => {
      if (res.status === PermissionStatus.UNDETERMINED) {
        void requestTrackingPermissionsAsync();
      }
    });
  }, []);

  // check system maintenance status on app start.
  useEffect(() => {
    void checkSystemStatus();
  }, []);

  // echo will send a network connection which will trigger the
  // ios network connection permission dialog.
  useEcho();

  useWeixinIntegration();

  useLockScreenOrientation();

  const isHub = useIsHub();

  const colorMode = useColorMode(
    isHub ? kDefaultPresentationColorMode : undefined,
  );
  const iconFontsLoaded = useIcomoon();
  const [ready, setReady] = useState(false);
  const [launched, setLaunched] = useState(false);

  const onLaunch = useCallback(() => {
    setLaunched(true);
  }, []);

  const onReady = useCallback(() => {
    delayed(() => {
      setReady(true);
    }, 0);
  }, []);

  const onStartPlayAnimation = useCallback(() => {
    delayed(() => {
      void SplashScreen.hideAsync();
    }, 100);
  }, []);

  useEffect(() => {
    if (Platform.OS === 'ios') {
      void clearIos18VideoAssetsCache();
    }
  }, []);

  if (!colorMode || !iconFontsLoaded) {
    return null;
  }

  const TouchEventBoundary = config.sentry.enabled
    ? TouchEventBoundarySentryImpl
    : Fragment;

  return (
    // Note(eric): for `react-native-gesture-handler@2.3.0+`:
    // If you use props such as shouldCancelWhenOutside, simultaneousHandlers,
    // waitFor etc. with gesture handlers, the handlers need to be mounted under
    // a single GestureHandlerRootView. So it's important to keep the
    // GestureHandlerRootView as close to the actual root view as possible.
    // Note that GestureHandlerRootView acts like a normal View. So if you want
    // it to fill the screen, you will need to pass { flex: 1 } like you'll need
    // to do with a normal View. By default, it'll take the size of the content
    // nested inside, see the following links for more information:
    // - https://docs.swmansion.com/react-native-gesture-handler/docs/#js
    // - https://blog.expo.dev/expo-sdk-44-4c4b8306584a

    <Suspense fallback={null}>
      <ResponsiveContextProvider>
        <ServiceFactoryProvider>
          {/** @ts-ignore */}
          <ThemeProvider theme={getColorTheme(colorMode)}>
            <PresentationThemeProvider colorMode={colorMode}>
              <PresentationModeProvider>
                <RootViewContainer>
                  <AppStatusBarContextProvider name="global">
                    <TouchEventBoundary>
                      <SafeAreaProvider>
                        <SlotFillProvider>
                          <HoldItemProvider>
                            <VoiceRecordingProvider>
                              <AppModalProvider>
                                <ActionSheetProvider>
                                  <ImageViewerContextProvider>
                                    <LoadingProvider>
                                      <AutomaticKeyboardMonitor />
                                      <RootSiblingParent>
                                        <Suspense fallback={null}>
                                          <Tracking />
                                        </Suspense>
                                        {launched && (
                                          <Content onReady={onReady} />
                                        )}
                                      </RootSiblingParent>
                                    </LoadingProvider>
                                  </ImageViewerContextProvider>
                                </ActionSheetProvider>
                                <FloatingTextInput />
                              </AppModalProvider>
                            </VoiceRecordingProvider>
                          </HoldItemProvider>
                          <Slot name="portal" />
                        </SlotFillProvider>
                      </SafeAreaProvider>
                    </TouchEventBoundary>
                  </AppStatusBarContextProvider>
                </RootViewContainer>
                {(!ready || !launched) && (
                  <LaunchScreen
                    colorMode={colorMode}
                    onBeginPlayAnimation={onStartPlayAnimation}
                    onLaunch={onLaunch}
                  />
                )}
              </PresentationModeProvider>
            </PresentationThemeProvider>
          </ThemeProvider>
        </ServiceFactoryProvider>
      </ResponsiveContextProvider>
    </Suspense>
  );
};

const RootViewContainer = memo(({ children }: { children?: ReactNode }) => {
  const mode = usePresentationMode();
  const theme = usePresentationTheme();

  const onTouch = useCallback(() => {
    appTouch$.next(true);
  }, []);

  return (
    <GestureHandlerRootView
      onTouchStart={onTouch}
      key={mode}
      style={{
        flex: 1,
        backgroundColor: mode === 'presentation' ? theme.page.bg : undefined,
      }}
    >
      <StatusBar style="dark" hidden={mode === 'presentation'} />
      {children}
    </GestureHandlerRootView>
  );
});

/**
 * Builds the viewer's chat identity from the auth context and hands it to
 * the app-wide `ChatSessionProvider`. Unauthenticated viewers get a
 * fully-shaped placeholder (see `makePlaceholderChatIdentity`) so a logout
 * re-render can't crash.
 */
const ChatSession = memo(({ children }: { children: ReactNode }) => {
  const currentUser = useCurrentUser();
  const identity = React.useMemo<ChatUserIdentity>(
    () =>
      currentUser
        ? {
            platformUserId: currentUser.app.userId,
            platformUid: currentUser.platformUid,
            platformAppId: currentUser.platformAppId,
            user: currentUser.user,
            app: currentUser.app,
          }
        : makePlaceholderChatIdentity(),
    [currentUser],
  );
  return (
    <ChatSessionProvider identity={identity}>{children}</ChatSessionProvider>
  );
});

const Content = memo(({ onReady }: { onReady?: () => void }) => {
  const authContextKey = useAuthContextKey();

  const [maintenanceKey, setMaintenanceKey] = useState(0);
  useEffect(() => {
    const sub = maintenanceResolved$.subscribe(() => {
      setMaintenanceKey(k => k + 1);
    });
    return () => sub.unsubscribe();
  }, []);

  const contentKey = `${authContextKey}:${maintenanceKey}`;

  return (
    <AuthContextProvider
      key={contentKey}
      currentContextKey={authContextKey}
      onServerUnreachable={onReady}
    >
      <AIAssistantProvider>
        <Fonts />
        <WeWorkRegistration />
        {/* ChatSession holds the app-wide chat state — the WS connection
            manager, unread total, active-room/list signals. Mounted inside
            the keyed AuthContextProvider so it tears down on account switch,
            and above RoutesContainer so it covers every consumer: the
            app-shell unread badge, the inbox, and the foreground push
            handler. Unauthenticated viewers get a dummy identity. The AI
            FAB is intentionally OUTSIDE it (it's decoupled from chat). */}
        <ChatSession>
          <FeedUnreadProvider>
            <FloatingChatProvider>
              <RoutesContainer onReady={onReady}>
                {/* Shared by the flows (which register the focused task /
                    service record) and the FloatingChatHost (which reads it). */}
                <ActiveEntityProvider>
                  <TaskStateTrackingProvider>
                    <Routes />
                  </TaskStateTrackingProvider>
                  <SessionTracker />
                  <TokenRefreshScheduler />
                  <WiredSnackbar />
                  <UpdatesCheck />
                  <TrackVersionUpgrade />
                  {config.cloudPush.enabled && <NotificationHandlers />}
                  {/* Inside the nav container: the chat coordinator uses
                      `useNavigation` to open cards. */}
                  <FloatingChatHost />
                </ActiveEntityProvider>
              </RoutesContainer>
            </FloatingChatProvider>
          </FeedUnreadProvider>
        </ChatSession>
        <AIAssistant />
        <WiredScreenSaver />
        <MaintenanceScreen />
        <Suspense fallback={null}>
          {Platform.OS === 'ios' && <UseHook hook={useTrackingTransparency} />}
          <FlushReportSharingLogs />
        </Suspense>
      </AIAssistantProvider>
    </AuthContextProvider>
  );
});

export default __DEV__
  ? App
  : config.sentry.enabled
  ? withErrorBoundarySentryImpl(App, {
      fallback: (props: any) => <ErrorFallback {...props} />,
    } as any)
  : withErrorBoundaryReactImpl(App, {
      fallback: (props: any) => <ErrorFallback {...props} />,
    });

function withErrorBoundaryReactImpl<T extends object>(
  Component: ComponentType<T>,
  options: {
    fallback: ComponentType<FallbackComponentProps>;
  },
) {
  return (props: T) => {
    return (
      // @ts-ignore
      <ReactNativeErrorBoundary FallbackComponent={options.fallback}>
        <Component {...props} />
      </ReactNativeErrorBoundary>
    );
  };
}
